// frontend/src/pages/admin/EditArticlePage.tsx
import { useState, useEffect, type FormEvent, type ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { ArrowLeft, Save, Loader2, AlertCircle, CheckCircle, Eye, Code } from 'lucide-react';
import RichTextEditor from '@/components/RichTextEditor';
import { getArticleByIdOrSlug, updateArticle, uploadArticleImage } from '@/api/articleService';
import { getAllTags } from '@/api/tagService';
import { type TagPublic, type ArticleUpdatePayload as ArticleUpdateType, type Article as ArticleDetailType } from '@/types';

const slugify = (text: string): string => {
  return text.toString().toLowerCase().trim().replace(/\s+/g, '-').replace(/[^\w-]+/g, '').replace(/--+/g, '-');
};

function unescapeHtml(html: string): string {
  if (typeof document === 'undefined') { // Basic SSR guard
    return html
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#039;/g, "'");
  }
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
}

const EditArticlePage = () => {
  const navigate = useNavigate();
  const { articleId } = useParams<{ articleId: string }>();

  const [initialArticleData, setInitialArticleData] = useState<ArticleDetailType | null>(null);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [summary, setSummary] = useState('');
  const [body, setBody] = useState('');
  const [status, setStatus] = useState<'draft' | 'published' | 'archived'>('draft');
  
  const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [allTags, setAllTags] = useState<TagPublic[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const [showRawHtml, setShowRawHtml] = useState(true); 

  const backendBaseUrl = import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '') || 'http://localhost:8000';

  // Helper to construct full image URL
  const getFullStaticUrl = (relativePath: string | null | undefined): string | null => {
    if (!relativePath) return null;
    if (relativePath.startsWith('http')) return relativePath; // Already a full URL
    // Ensure it handles cases where relativePath might incorrectly start with /static/
    if (relativePath.startsWith('/static/')) {
        return `${backendBaseUrl}${relativePath}`;
    }
    return `${backendBaseUrl}/static/${relativePath}`;
  };

  useEffect(() => {
    const fetchArticleAndTags = async () => {
      if (!articleId) {
        setError("Article ID is missing."); setIsLoading(false); return;
      }
      try {
        setIsLoading(true);
        const [articleData, tagsData] = await Promise.all([
          getArticleByIdOrSlug(articleId),
          getAllTags()
        ]);
        
        setInitialArticleData(articleData);
        setTitle(articleData.title);
        setSlug(articleData.slug);
        setSummary(articleData.summary ? unescapeHtml(articleData.summary) : '');
        setBody(articleData.body ? unescapeHtml(articleData.body) : '');
        setStatus(articleData.status as 'draft' | 'published' | 'archived');
        
        setCurrentImageUrl(articleData.image_url || null);
        setImagePreview(getFullStaticUrl(articleData.image_url)); // Use helper for initial preview

        setSelectedTagIds(articleData.tags.map(tag => tag.id));
        setAllTags(tagsData);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch article or tags:", err);
        setError("Failed to load article data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchArticleAndTags();
  }, [articleId, backendBaseUrl]); // backendBaseUrl added as dep for getFullStaticUrl if it were inside useEffect

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    setTitle(newTitle);
    if (initialArticleData && (slug === slugify(initialArticleData.title) || !slug)) {
      setSlug(slugify(newTitle));
    }
  };

  const handleTagSelection = (tagIdString: string) => {
    const id = parseInt(tagIdString, 10);
    setSelectedTagIds(prev => prev.includes(id) ? prev.filter(tId => tId !== id) : [...prev, id]);
  };

  const handleBodyChange = (content: string) => {
    setBody(content);
  };

  const handleImageFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setError("Invalid file type. Please select an image.");
        setImageFile(null);
        // Keep existing preview if currentImageUrl exists
        setImagePreview(getFullStaticUrl(currentImageUrl || initialArticleData?.image_url));
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file)); // Preview new local file
      setError(null);
      setIsUploadingImage(true);
      try {
        const response = await uploadArticleImage(file);
        setCurrentImageUrl(response.image_url); // Store relative path from backend
        // After successful upload, imagePreview can remain the blob URL,
        // or you can update it to the newly formed backend URL if preferred, but it's not strictly necessary
        // as currentImageUrl is what will be saved.
        console.log("Image uploaded, new relative URL:", response.image_url);
      } catch (uploadError: any) {
        console.error("Failed to upload image:", uploadError);
        setError(uploadError.response?.data?.detail || "Image upload failed. Please try again.");
        // Revert preview to the original/current image if upload fails
        setImagePreview(getFullStaticUrl(initialArticleData?.image_url));
        setCurrentImageUrl(initialArticleData?.image_url || null); // Revert currentImageUrl
      } finally {
        setIsUploadingImage(false);
      }
    } else { 
      setImageFile(null);
      // If file input is cleared, revert to displaying the initial/current image from the server
      setImagePreview(getFullStaticUrl(currentImageUrl || initialArticleData?.image_url));
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!articleId) { setError("Article ID is missing for update."); return; }
    setIsSubmitting(true); setError(null); setSuccessMessage(null);

    if (!title || !slug || !body) {
      setError("Title, slug, and body are required."); setIsSubmitting(false); return;
    }

    const payload: ArticleUpdateType = {
      title, slug, summary: summary || null, body, status,
      image_url: currentImageUrl, 
      tag_ids: selectedTagIds.length > 0 ? selectedTagIds : null,
    };

    try {
      const updatedArticle = await updateArticle(parseInt(articleId, 10), payload);
      setSuccessMessage(`Article "${updatedArticle.title}" updated successfully! Redirecting...`);
      // Update initialArticleData to reflect the saved state, especially for image_url
      setInitialArticleData(updatedArticle);
      setCurrentImageUrl(updatedArticle.image_url || null);
      setImagePreview(getFullStaticUrl(updatedArticle.image_url));

      setTimeout(() => navigate('/admin/articles'), 2000);
    } catch (err: any) {
      console.error("Failed to update article:", err);
      setError(err.response?.data?.detail || "Failed to update article.");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (isLoading) { 
    return (
      <div className="container mx-auto px-4 py-20 flex flex-col items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
        <p className="text-lg text-muted-foreground">Loading article data...</p>
      </div>
    );
  }
  if (error && !initialArticleData) { 
     return (
      <div className="container mx-auto px-4 py-20 text-center">
        <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-6" />
        <h2 className="text-2xl font-semibold text-destructive mb-2">Error Loading Article</h2>
        <p className="text-muted-foreground mb-6">{error}</p>
        <Button variant="outline" onClick={() => navigate(-1)} className='cursor-pointer'>
          <ArrowLeft className="mr-2 h-4 w-4" /> Go Back
        </Button>
      </div>
    );
  }
  if (!initialArticleData && !isLoading) { // Fallback if not loading and no data
    return (
      <div className="container mx-auto px-4 py-20 text-center">Article not found or failed to load.</div>
    );
  }


  return (
    <div className="container mx-auto px-4 py-8 md:py-12">
      <div className="mb-6">
        <Button variant="outline" onClick={() => navigate("/admin/articles")} className="text-primary hover:text-primary/80 cursor-pointer">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Manage Articles
        </Button>
      </div>

      <Card className="max-w-3xl mx-auto bg-card border-border/30 shadow-lg">
        <CardHeader>
          <CardTitle className="text-2xl md:text-3xl text-foreground">Edit Article</CardTitle>
          <CardDescription className="text-muted-foreground">Modify the article details below.</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-6">
            {error && ( <div className="p-3 bg-destructive/10 text-destructive border border-destructive/30 rounded-md flex items-center"> <AlertCircle className="h-5 w-5 mr-2" /> {error} </div> )}
            {successMessage && ( <div className="p-3 bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30 rounded-md flex items-center"> <CheckCircle className="h-5 w-5 mr-2" /> {successMessage} </div> )}
            
            <div className="space-y-2">
              <Label htmlFor="title" className="text-foreground">Title</Label>
              <Input id="title" value={title} onChange={handleTitleChange} required disabled={isSubmitting} className="!bg-input"/>
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug" className="text-foreground">Slug</Label>
              <Input id="slug" value={slug} onChange={(e) => setSlug(e.target.value)} required disabled={isSubmitting} className="!bg-input"/>
              <p className="text-xs text-muted-foreground">URL-friendly version of the title.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="summary" className="text-foreground">Summary (Optional)</Label>
              <Textarea id="summary" value={summary} onChange={(e) => setSummary(e.target.value)} rows={3} className="!bg-input min-h-[80px]" disabled={isSubmitting}/>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center mb-1">
                <Label htmlFor="body" className="text-foreground">Body</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowRawHtml(!showRawHtml)} className="text-xs cursor-pointer">
                  {showRawHtml ? <><Eye className="mr-1 h-3 w-3" /> View Rendered (Rich Text)</> : <><Code className="mr-1 h-3 w-3" /> View Raw HTML</>}
                </Button>
              </div>
              {showRawHtml ? (
                <Textarea value={body} onChange={(e) => handleBodyChange(e.target.value)} className="w-full min-h-[250px] p-2 border-input rounded-md font-mono text-sm bg-muted/30" disabled={isSubmitting}/>
              ) : (
                <div className="border-input rounded-md p-1 bg-background shadow-sm">
                  <RichTextEditor key={initialArticleData?.id || 'edit-article'} value={body} onChange={handleBodyChange} />
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="articleImage" className="text-foreground">Article Image (Optional)</Label>
              <div className="flex items-center gap-4">
                <Input
                  id="articleImage"
                  type="file"
                  accept="image/*"
                  onChange={handleImageFileChange}
                  className="!bg-input border-border focus:border-primary focus:ring-primary file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20 cursor-pointer"
                  disabled={isUploadingImage || isSubmitting}
                />
                {isUploadingImage && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
              </div>
              {imagePreview && (
                <div className="mt-2">
                  <img src={imagePreview} alt="Preview" className="h-32 w-auto object-contain border border-border rounded-md p-1 bg-muted/10" />
                  {currentImageUrl && !imageFile && <p className="text-xs text-muted-foreground mt-1">Current image: {currentImageUrl.split('/').pop()}</p> }
                  {imageFile && currentImageUrl && <p className="text-xs text-green-600 mt-1">New image selected and uploaded. Relative path: {currentImageUrl}</p>}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="status" className="text-foreground">Status</Label>
                <Select value={status} onValueChange={(value: 'draft' | 'published' | 'archived') => setStatus(value)} disabled={isSubmitting}>
                  <SelectTrigger className="!bg-input"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="published">Published</SelectItem><SelectItem value="archived">Archived</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2 pb-4">
                <Label className="text-foreground">Tags (Optional)</Label>
                <div className="flex flex-wrap gap-2 p-3 border border-input rounded-md min-h-[40px] bg-background">
                    {allTags.length > 0 ? allTags.map(tag => (
                        <Button
                            type="button"
                            key={tag.id}
                            variant={selectedTagIds.includes(tag.id) ? "default" : "outline"}
                            size="sm"
                            onClick={() => handleTagSelection(String(tag.id))}
                            className={`transition-all cursor-pointer ${selectedTagIds.includes(tag.id) ? '!bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:bg-accent/10'}`}
                            disabled={isSubmitting}
                        >
                            {tag.name}
                        </Button>
                    )) : <p className="text-sm text-muted-foreground">No tags available or loading...</p>}
                </div>
            </div>
          </CardContent>
          <CardFooter className="border-t border-border/30 pt-6">
            <Button type="submit" className="w-full md:w-auto !bg-gradient-to-r !from-primary !to-accent" disabled={isSubmitting || isLoading || isUploadingImage}>
              {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating Article...</> : <><Save className="mr-2 h-4 w-4" /> Update Article</>}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default EditArticlePage;